import { addDays, addMonths, format, isAfter, isBefore, isSameDay } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { getDateFnsLocale } from '../../../utils/dateUtils';
import { useNonce } from '../../hooks/use-nonce';
import { DateOnly } from '../../utils/date/date-only';
import { MonthOnly } from '../../utils/date/month-only';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { LanguageProvider } from '../language-provider/language-provider';
import { Calendar } from './components/calendar';
import { CalendarHeader } from './components/calendar-header';
import { TimeHeader } from './components/time-header';
import { TimeScrollBars } from './components/time-scroll-bars';

type Props = {
  startDate: Date;
  /** Optional end date for range mode initial value */
  endDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  displayTime: boolean;
  /** Enable date-time RANGE selection. When true, two pickers are shown and onRangeChoosen is used. */
  range?: boolean;
  onDateChoosen: (date: Date) => void;
  onRangeChoosen?: (from: Date, to: Date) => void;
  onCancel: () => void;
};

const DateTimePickerDialog = (props: Props) => {
  const { t } = useTranslation();
  const isRange = Boolean(props.range && props.onRangeChoosen);

  // Single-mode state
  const [currentMonth, setCurrentMonth] = React.useState(MonthOnly.fromDate(props.startDate));
  const [selectedDate, setSelectedDate] = React.useState(DateOnly.fromDate(props.startDate));
  const [hour, setHour] = React.useState(props.startDate.getHours());
  const [minute, setMinute] = React.useState(props.startDate.getMinutes());
  const [scrollIntoViewNonce, refreshScrollIntoViewNonce] = useNonce();

  // Range-mode state (From)
  const [currentMonthFrom, setCurrentMonthFrom] = React.useState(
    MonthOnly.fromDate(props.startDate),
  );
  const [selectedDateFrom, setSelectedDateFrom] = React.useState(
    DateOnly.fromDate(props.startDate),
  );

  // Range-mode state (To)
  const initialEnd = props.endDate ?? props.startDate;
  // removed unused secondary month state in simplified range mode
  // const [currentMonthTo, setCurrentMonthTo] = React.useState(MonthOnly.fromDate(initialEnd));
  const [selectedDateTo, setSelectedDateTo] = React.useState(DateOnly.fromDate(initialEnd));

  const getSelectedDateTime = () => {
    return props.displayTime
      ? new Date(selectedDate.toDate().setHours(hour, minute))
      : selectedDate.toDate();
  };

  const getSelectedDateTimeFrom = () => {
    return selectedDateFrom.toDate();
  };

  const getSelectedDateTimeTo = () => {
    return selectedDateTo.toDate();
  };

  const onTimeChanged = (hour: number, minute: number) => {
    setHour(hour);
    setMinute(minute);
  };

  /**
   * Strips time from date if props.displayTime is false.
   */
  const stripTime = (date: Date) => {
    return props.displayTime ? date : DateOnly.fromDate(date).toDate();
  };

  const stripTimeFrom = (date: Date) => DateOnly.fromDate(date).toDate();
  const stripTimeTo = (date: Date) => DateOnly.fromDate(date).toDate();

  const isSelectedDateTimeValid = () => {
    const selectedDate = getSelectedDateTime();

    if (props.minDate && isBefore(selectedDate, stripTime(props.minDate))) {
      return false;
    }

    if (props.maxDate && isAfter(selectedDate, stripTime(props.maxDate))) {
      return false;
    }

    return true;
  };

  const isSelectedDateTimeValidFrom = () => {
    const d = getSelectedDateTimeFrom();
    const maxBound = props.maxDate ? stripTimeFrom(props.maxDate) : undefined;
    const minBound = props.minDate ? stripTimeFrom(props.minDate) : undefined;
    // In range mode, "from" cannot be after selected "to"
    const toBound = stripTimeFrom(getSelectedDateTimeTo());
    if (minBound && isBefore(d, minBound)) {
      return false;
    }
    if (maxBound && isAfter(d, maxBound)) {
      return false;
    }
    if (isAfter(d, toBound)) {
      return false;
    }
    return true;
  };
  const resolveMinHour = () => {
    return props.minDate && isSameDay(props.minDate, selectedDate.toDate())
      ? props.minDate.getHours()
      : undefined;
  };

  const resolveMaxHour = () => {
    return props.maxDate && isSameDay(props.maxDate, selectedDate.toDate())
      ? props.maxDate.getHours()
      : undefined;
  };

  const resolveMinMinute = () => {
    return props.minDate &&
      isSameDay(props.minDate, selectedDate.toDate()) &&
      hour === resolveMinHour()
      ? props.minDate.getMinutes()
      : undefined;
  };

  const resolveMaxMinute = () => {
    return props.maxDate &&
      isSameDay(props.maxDate, selectedDate.toDate()) &&
      hour === resolveMaxHour()
      ? props.maxDate.getMinutes()
      : undefined;
  };

  const isSelectedDateTimeValidTo = () => {
    const d = getSelectedDateTimeTo();
    const maxBound = props.maxDate ? stripTimeTo(props.maxDate) : undefined;
    const minBound = props.minDate ? stripTimeTo(props.minDate) : undefined;
    // In range mode, "to" cannot be before selected "from"
    const fromBound = stripTimeTo(getSelectedDateTimeFrom());
    if (minBound && isBefore(d, minBound)) {
      return false;
    }
    if (maxBound && isAfter(d, maxBound)) {
      return false;
    }
    if (isBefore(d, fromBound)) {
      return false;
    }
    return true;
  };

  const getHeaderText = () => {
    if (!isRange) {
      return format(getSelectedDateTime(), props.displayTime ? 'PPPPp' : 'PPPP', {
        locale: getDateFnsLocale(),
      });
    }
    const from = getSelectedDateTimeFrom();
    const to = getSelectedDateTimeTo();
    const fmt = 'PP';
    return `${format(from, fmt, { locale: getDateFnsLocale() })} → ${format(to, fmt, {
      locale: getDateFnsLocale(),
    })}`;
  };

  const getCalendarTitle = () => {
    const monthName = format(currentMonth.toDate(), 'MMMM', { locale: getDateFnsLocale() });
    return `${monthName} ${currentMonth.year}`;
  };
  const getCalendarTitleFrom = () => {
    const monthName = format(currentMonthFrom.toDate(), 'MMMM', { locale: getDateFnsLocale() });
    return `${monthName} ${currentMonthFrom.year}`;
  };

  const onPrevMonth = () => {
    setCurrentMonth(MonthOnly.fromDate(addMonths(currentMonth.toDate(), -1)));
  };

  const onNextMonth = () => {
    setCurrentMonth(MonthOnly.fromDate(addMonths(currentMonth.toDate(), 1)));
  };
  const onPrevMonthFrom = () => {
    setCurrentMonthFrom(MonthOnly.fromDate(addMonths(currentMonthFrom.toDate(), -1)));
  };
  const onNextMonthFrom = () => {
    setCurrentMonthFrom(MonthOnly.fromDate(addMonths(currentMonthFrom.toDate(), 1)));
  };

  const onToday = () => {
    const now = new Date();
    setCurrentMonth(MonthOnly.fromDate(now));
    setSelectedDate(DateOnly.fromDate(now));
    setHour(now.getHours());
    setMinute(now.getMinutes());

    refreshScrollIntoViewNonce();
  };
  const onTodayRange = () => {
    const now = new Date();
    setCurrentMonthFrom(MonthOnly.fromDate(now));
    setSelectedDateFrom(DateOnly.fromDate(now));
    // single-calendar range mode
    setSelectedDateTo(DateOnly.fromDate(now));
  };

  const onChoose = () => {
    if (isRange && props.onRangeChoosen) {
      props.onRangeChoosen(getSelectedDateTimeFrom(), getSelectedDateTimeTo());
      return;
    }
    props.onDateChoosen(getSelectedDateTime());
  };

  // Unused helpers in simplified picker kept for backward compatibility above

  // Unused helpers in simplified picker kept for backward compatibility above

  return (
    <LanguageProvider>
      <Dialog closeIcon={true} closeOnDimmerClick={true} onClose={props.onCancel}>
        {{
          header: getHeaderText(),
          content: isRange ? (
            <Content displayTime={false}>
              <CalendarHeader
                title={getCalendarTitleFrom()}
                onNextMonth={onNextMonthFrom}
                onPrevMonth={onPrevMonthFrom}
                onTitleClick={() => {
                  // Select the entire current month
                  const start = new Date(currentMonthFrom.year, currentMonthFrom.month, 1);
                  const end = new Date(currentMonthFrom.year, currentMonthFrom.month + 1, 0);
                  setSelectedDateFrom(DateOnly.fromDate(start));
                  setSelectedDateTo(DateOnly.fromDate(end));
                }}
              />
              <Calendar
                currentMonth={currentMonthFrom}
                maxDate={props.maxDate && DateOnly.fromDate(props.maxDate)}
                minDate={props.minDate && DateOnly.fromDate(props.minDate)}
                rangeFrom={selectedDateFrom}
                rangeTo={selectedDateTo}
                selectedDate={selectedDateFrom}
                onDateSelected={(dateOnly) => {
                  const clicked = dateOnly.toDate();
                  const from = selectedDateFrom.toDate();
                  const to = selectedDateTo.toDate();
                  if (clicked < from) {
                    setSelectedDateFrom(dateOnly);
                  } else if (clicked > to) {
                    setSelectedDateTo(dateOnly);
                  } else if (isSameDay(clicked, from) || isSameDay(clicked, to)) {
                    setSelectedDateFrom(dateOnly);
                    setSelectedDateTo(dateOnly);
                  } else {
                    // inside range: use handles to adjust
                  }
                  setCurrentMonthFrom(MonthOnly.fromDateOnly(dateOnly));
                }}
                onNumWeeksChanged={() => undefined}
                onRangeHandleClick={(dateOnly, which) => {
                  if (which === 'start') {
                    setSelectedDateFrom(dateOnly);
                    if (dateOnly.toDate() > selectedDateTo.toDate()) {
                      setSelectedDateTo(dateOnly);
                    }
                  } else {
                    setSelectedDateTo(dateOnly);
                    if (dateOnly.toDate() < selectedDateFrom.toDate()) {
                      setSelectedDateFrom(dateOnly);
                    }
                  }
                }}
                onWeekNumberClick={(weekStartDate) => {
                  // Select the entire week starting at weekStartDate
                  const start = weekStartDate.toDate();
                  const end = addDays(start, 6);
                  setSelectedDateFrom(weekStartDate);
                  setSelectedDateTo(DateOnly.fromDate(end));
                }}
              />
            </Content>
          ) : (
            <Content displayTime={props.displayTime}>
              <CalendarHeader
                title={getCalendarTitle()}
                onNextMonth={onNextMonth}
                onPrevMonth={onPrevMonth}
              />

              <Calendar
                currentMonth={currentMonth}
                maxDate={props.maxDate && DateOnly.fromDate(props.maxDate)}
                minDate={props.minDate && DateOnly.fromDate(props.minDate)}
                selectedDate={selectedDate}
                onDateSelected={(date) => {
                  setSelectedDate(date);
                  setCurrentMonth(MonthOnly.fromDateOnly(date));
                }}
                onNumWeeksChanged={() => undefined}
              />

              {props.displayTime && (
                <>
                  <TimeHeader hour={hour} minute={minute} />

                  <TimeScrollBars
                    hour={hour}
                    maxHour={resolveMaxHour()}
                    maxMinute={resolveMaxMinute()}
                    minHour={resolveMinHour()}
                    minMinute={resolveMinMinute()}
                    minute={minute}
                    numVisibleItems={7}
                    scrollIntoViewNonce={scrollIntoViewNonce}
                    onTimeChanged={onTimeChanged}
                  />
                </>
              )}
            </Content>
          ),
          footer: {
            left: isRange ? (
              <Button variant="contained" onClick={onTodayRange}>
                {t('today', { ns: 'common' })}
              </Button>
            ) : (
              <Button variant="contained" onClick={onToday}>
                {t('today', { ns: 'common' })}
              </Button>
            ),
            right: (
              <>
                <Button
                  color="primary"
                  disabled={
                    isRange
                      ? !(
                          isSelectedDateTimeValidFrom() &&
                          isSelectedDateTimeValidTo() &&
                          !isAfter(getSelectedDateTimeFrom(), getSelectedDateTimeTo())
                        )
                      : !isSelectedDateTimeValid()
                  }
                  variant="contained"
                  onClick={onChoose}
                >
                  {t('choose', { ns: 'common' })}
                </Button>
                <Button variant="text" onClick={props.onCancel}>
                  {t('close', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>
    </LanguageProvider>
  );
};

const Content = styled.div<Pick<Props, 'displayTime'>>`
  display: grid;
  gap: 15px;

  ${(props) =>
    props.displayTime
      ? css`
          grid-template-areas:
            'calendar-header time-header'
            'calendar time-scroll-bars';
        `
      : css`
          grid-template-areas:
            'calendar-header'
            'calendar';
        `}

  ${CalendarHeader.styled} {
    grid-area: calendar-header;
  }
  ${Calendar.styled} {
    grid-area: calendar;
  }
  /* time UI removed in simplified range mode */
`;

export { DateTimePickerDialog, Props as DateTimePickerDialogProps };
